import React, { useRef, useEffect } from "react";
import { observer } from "mobx-react";
import { SALESFORCE_OAUTH_URL } from "Static/constant";
import { openConnectPopup } from "Pages/Dashboard/connectApps/oauthPopupBridge";
import { salesforceConnectedData } from "Pages/Dashboard/settings/Crm/salesforce/SalesforceConnectedData";
import copilotStore from "Stores/copilotStore";
import salesForceIcon from "Assets/png/salesforce.png";

// Demo default = a mock popup that plays "Signing in -> Connected" and closes
// itself. Set localStorage sg_copilot_real_oauth = "1" to use the real
// Salesforce OAuth flow (same recipe as SalesforceConfigure) instead.
const REAL_OAUTH_FLAG = "sg_copilot_real_oauth";
const MOCK_POPUP_MS = 2600;

const openMockOauthPopup = (iconUrl, onDone) => {
  const left = Math.max(0, window.screen.width / 2 - 240);
  const top = Math.max(0, window.screen.height / 2 - 300);
  const popup = window.open(
    "",
    "Salesforce Login",
    `width=480,height=600,left=${left},top=${top}`
  );
  if (!popup) {
    // Popup blocked: keep the demo moving anyway.
    onDone();
    return;
  }
  popup.document.write(`<!DOCTYPE html>
<html>
<head>
<title>Log in | Salesforce</title>
<style>
  body { margin: 0; font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; background: #f3f4f8; display: flex; align-items: center; justify-content: center; height: 100vh; }
  .card { background: #fff; border-radius: 12px; box-shadow: 0 8px 28px rgba(29,32,38,.12); padding: 40px 44px; text-align: center; width: 300px; }
  .card img { width: 72px; margin-bottom: 18px; }
  .spinner { width: 26px; height: 26px; border: 3px solid #eceeff; border-top-color: #0176d3; border-radius: 50%; margin: 0 auto 14px; animation: spin .8s linear infinite; }
  .check { display: none; width: 44px; height: 44px; border-radius: 50%; background: #e8f8ef; color: #29b969; font-size: 26px; line-height: 44px; margin: 0 auto 14px; }
  h3 { font-size: 15px; color: #1d2026; margin: 0 0 6px; }
  p { font-size: 12px; color: #6b7280; margin: 0; }
  .done .spinner { display: none; }
  .done .check { display: block; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
</head>
<body>
  <div class="card" id="card">
    <img src="${iconUrl}" alt="Salesforce" />
    <div class="spinner"></div>
    <div class="check">&#10003;</div>
    <h3 id="msg">Signing in to Salesforce&hellip;</h3>
    <p id="sub">demo environment, no credentials needed</p>
  </div>
  <script>
    setTimeout(function () {
      document.getElementById("card").className = "card done";
      document.getElementById("msg").innerText = "Connected";
      document.getElementById("sub").innerText = "Returning to Salesgear\\u2026";
    }, 1400);
  </script>
</body>
</html>`);
  popup.document.close();
  setTimeout(() => {
    try {
      popup.close();
    } catch (e) {
      /* noop */
    }
    onDone();
  }, MOCK_POPUP_MS);
};

const ConnectIntegrationBlock = observer(({ block }) => {
  const busyRef = useRef(false);

  useEffect(() => () => {
    busyRef.current = false;
  }, []);

  const reportSuccess = () => {
    busyRef.current = false;
    copilotStore.sendActionResult(block.id, block.actionId, "success");
  };

  const handleConnect = () => {
    if (block.status === "connected" || copilotStore.streaming || busyRef.current) {
      return;
    }
    busyRef.current = true;

    if (localStorage.getItem(REAL_OAUTH_FLAG) === "1" && SALESFORCE_OAUTH_URL) {
      // Real flow: popup callback page calls notifyOpener, the bridge closes
      // the popup and delivers the result here.
      openConnectPopup({
        url: SALESFORCE_OAUTH_URL,
        name: "Salesforce Login",
        types: ["SALESFORCE_CONNECT_OAUTH"],
        onResult: () => {
          salesforceConnectedData.getSalesForceOauthData();
          reportSuccess();
        }
      });
      return;
    }

    const iconUrl = new URL(salesForceIcon, window.location.origin).href;
    openMockOauthPopup(iconUrl, reportSuccess);
  };

  const connected = block.status === "connected";

  return (
    <div className={`copilotConnect status-${block.status}`}>
      <div className="copilotConnect__icon">
        <img src={salesForceIcon} alt={`Connect ${block.provider}`} />
      </div>
      <div className="copilotConnect__meta">
        <div className="copilotConnect__label">{block.label}</div>
        {block.description && (
          <div className="copilotConnect__desc">{block.description}</div>
        )}
      </div>
      {connected ? (
        <div className="copilotConnect__connected">
          <i className="material-icons-outlined">check_circle</i>
          <span>Connected</span>
        </div>
      ) : (
        <button
          type="button"
          className="copilotConnect__btn"
          disabled={copilotStore.streaming}
          onClick={handleConnect}
        >
          {block.status === "failed" ? "Retry connection" : block.label}
        </button>
      )}
    </div>
  );
});

export default ConnectIntegrationBlock;
